import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ATTACHMENT_TYPE_IDS,
  type AttachmentHandlingMode,
  type AttachmentPolicyDraft,
  type AttachmentTypeId,
  type AttachmentTypeMode,
  type AttachmentTypePolicyDraft,
} from '../utils/attachmentPolicyModel';
import styles from './AiAttachmentPolicyEditor.module.css';

type AiAttachmentPolicyEditorProps = {
  value: AttachmentPolicyDraft;
  onChange: (value: AttachmentPolicyDraft) => void;
  disabled?: boolean;
};

const getTypeTranslationKey = (typeId: AttachmentTypeId): string =>
  `aiSettings.attachmentPolicyEditor.types.${typeId}.label`;

const getTypeDescriptionKey = (typeId: AttachmentTypeId): string =>
  `aiSettings.attachmentPolicyEditor.types.${typeId}.description`;

export const AiAttachmentPolicyEditor: React.FC<AiAttachmentPolicyEditorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation('admin');

  const updateDraft = (patch: Partial<AttachmentPolicyDraft>) => {
    onChange({ ...value, ...patch });
  };

  const updateType = (typeId: AttachmentTypeId, patch: Partial<AttachmentTypePolicyDraft>) => {
    onChange({
      ...value,
      types: {
        ...value.types,
        [typeId]: {
          ...value.types[typeId],
          ...patch,
        },
      },
    });
  };

  return (
    <div className={styles.editor}>
      <div className={styles.defaultModeCard}>
        <label className={styles.fieldLabel} htmlFor="attachment-policy-default-mode">
          {t('aiSettings.attachmentPolicyEditor.defaultModeLabel')}
        </label>
        <p className={styles.fieldHelp}>{t('aiSettings.attachmentPolicyEditor.defaultModeHelp')}</p>
        <select
          id="attachment-policy-default-mode"
          className={styles.select}
          value={value.defaultMode}
          disabled={disabled}
          onChange={(event) => updateDraft({ defaultMode: event.target.value as AttachmentHandlingMode })}
        >
          <option value="process">{t('aiSettings.attachmentPolicyEditor.modes.process')}</option>
          <option value="awareness">{t('aiSettings.attachmentPolicyEditor.modes.awareness')}</option>
        </select>
      </div>

      <div className={styles.globalInstructionCard}>
        <label className={styles.fieldLabel} htmlFor="attachment-policy-global-instruction">
          {t('aiSettings.attachmentPolicyEditor.globalInstructionLabel')}
        </label>
        <p className={styles.fieldHelp}>{t('aiSettings.attachmentPolicyEditor.globalInstructionHelp')}</p>
        <textarea
          id="attachment-policy-global-instruction"
          className={styles.textarea}
          rows={3}
          value={value.instruction}
          disabled={disabled}
          placeholder={t('aiSettings.attachmentPolicyEditor.globalInstructionPlaceholder')}
          onChange={(event) => updateDraft({ instruction: event.target.value })}
        />
      </div>

      <div className={styles.typeList}>
        <div className={styles.typeListHeader}>
          <h4 className={styles.typeListTitle}>{t('aiSettings.attachmentPolicyEditor.perTypeTitle')}</h4>
          <p className={styles.typeListHelp}>{t('aiSettings.attachmentPolicyEditor.perTypeHelp')}</p>
        </div>

        {ATTACHMENT_TYPE_IDS.map((typeId) => {
          const typeDraft = value.types[typeId];
          const effectiveMode = typeDraft.mode === 'inherit' ? value.defaultMode : typeDraft.mode;

          return (
            <div key={typeId} className={styles.typeCard}>
              <div className={styles.typeCardHeader}>
                <div>
                  <div className={styles.typeLabel}>{t(getTypeTranslationKey(typeId))}</div>
                  <div className={styles.typeDescription}>{t(getTypeDescriptionKey(typeId))}</div>
                </div>
                <span className={`${styles.modeBadge} ${effectiveMode === 'process' ? styles.modeBadgeProcess : styles.modeBadgeAwareness}`}>
                  {effectiveMode === 'process'
                    ? t('aiSettings.attachmentPolicyEditor.modes.processShort')
                    : t('aiSettings.attachmentPolicyEditor.modes.awarenessShort')}
                </span>
              </div>

              <div className={styles.typeFields}>
                <label className={styles.srOnly} htmlFor={`attachment-policy-mode-${typeId}`}>
                  {t('aiSettings.attachmentPolicyEditor.typeModeLabel', { type: t(getTypeTranslationKey(typeId)) })}
                </label>
                <select
                  id={`attachment-policy-mode-${typeId}`}
                  className={styles.select}
                  value={typeDraft.mode}
                  disabled={disabled}
                  onChange={(event) => updateType(typeId, { mode: event.target.value as AttachmentTypeMode })}
                >
                  <option value="inherit">{t('aiSettings.attachmentPolicyEditor.modes.inherit')}</option>
                  <option value="process">{t('aiSettings.attachmentPolicyEditor.modes.process')}</option>
                  <option value="awareness">{t('aiSettings.attachmentPolicyEditor.modes.awareness')}</option>
                </select>

                <textarea
                  className={styles.textarea}
                  rows={2}
                  value={typeDraft.instruction}
                  disabled={disabled}
                  placeholder={t('aiSettings.attachmentPolicyEditor.typeInstructionPlaceholder')}
                  onChange={(event) => updateType(typeId, { instruction: event.target.value })}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
